import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import './OrgStructure.css';

const FunctionalStructure = () => {
  const { organizationId } = useAuth();
  const [data, setData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [newResponsibility, setNewResponsibility] = useState('');
  const [editResponsibility, setEditResponsibility] = useState('');
  const [newJob, setNewJob] = useState({
    role_title: '',
    level: '',
    department_id: '',
    purpose: '',
    responsibilities: []
  });
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editJob, setEditJob] = useState({
    role_id: null,
    role_title: '',
    role_grade: '',
    level: '',
    department_id: '',
    purpose: '',
    responsibilities: []
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, role: null });
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [newRespEditingIndex, setNewRespEditingIndex] = useState(null);
  const [newRespEditingValue, setNewRespEditingValue] = useState('');
  const [editRespEditingIndex, setEditRespEditingIndex] = useState(null);
  const [editRespEditingValue, setEditRespEditingValue] = useState('');

  // Level descriptions
  const levels = [
    { 
      value: 'Level 1', 
      label: 'Level 1: Entry level',
      description: 'Targeted persona: Team member with minimum entry requirements for their role in the team'
    },
    { 
      value: 'Level 2', 
      label: 'Level 2: Operational excellence',
      description: 'Targeted persona: Leads a defined team within established guidelines and clear escalation procedures.'
    },
    { 
      value: 'Level 3', 
      label: 'Level 3: Functional accountability',
      description: 'Targeted persona: Responsible for performance across different teams responsible for the achievement of varied, but related objectives.'
    },
    { 
      value: 'Level 4', 
      label: 'Level 4: Growth and sustainability',
      description: 'Targeted persona: Sets strategy, directs and coordinates execution with ultimate responsibility for results.'
    }
  ];

const parseResponsibilities = (responsibilities) => {
    if (!responsibilities) return [];
    if (Array.isArray(responsibilities)) return responsibilities;

    return responsibilities
      .split('\n')
      .map(item => item.replace(/^-\s*/, '').trim())
      .filter(Boolean);
  };

  const formatResponsibilitiesForStorage = (responsibilities) => {
    if (!responsibilities || responsibilities.length === 0) return '';
    return responsibilities.map(item => `- ${item}`).join('\n');
  };

  // Fetch departments scoped to this organisation
  useEffect(() => {
    if (!organizationId) return;
    const fetchDepartments = async () => {
      try {
        const { data: depts, error } = await supabase
          .from('department')
          .select('department_id, depart_name')
          .eq('organization_id', organizationId)
          .order('depart_name');

        if (error) throw error;

        setDepartments(depts || []);
        
        const peopleCulture = depts?.find(d => 
          d.depart_name.toLowerCase().includes('people') && 
          d.depart_name.toLowerCase().includes('culture')
        );
        
        if (peopleCulture) {
          setSelectedDepartment(peopleCulture.department_id);
        } else if (depts && depts.length > 0) {
          setSelectedDepartment(depts[0].department_id);
        }
      } catch (err) {
        console.error('Error fetching departments:', err.message);
      }
    };

    fetchDepartments();
  }, [organizationId]);

  // Fetch roles based on selected department
  useEffect(() => {
    if (!selectedDepartment) return;

    const fetchRoles = async () => {
      try {
        const { data: rolesData, error } = await supabase
          .from('roles')
          .select('*')
          .eq('department_id', selectedDepartment)
          .eq('organization_id', organizationId)
          .order('role_title');

        if (error) throw error;

        setData(rolesData || []);
      } catch (err) {
        console.error('Error fetching roles:', err.message);
      }
    };

    fetchRoles();
  }, [selectedDepartment, organizationId]);

  // Define the table columns
  const columns = useMemo(() => [
    { accessorKey: 'role_title', header: 'Job Title' },
    { accessorKey: 'level', header: 'Level' },
    {
      accessorKey: 'action',
      header: 'Action',
      cell: ({ row }) => (
        <div className="action-buttons-org">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleEdit(row.original); }}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDeleteClick(row.original); }}
          >
            Delete
          </button>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleAddResponsibility = () => {
    const trimmedResponsibility = newResponsibility.trim();
    if (!trimmedResponsibility) return;

    setNewJob(prev => ({
      ...prev,
      responsibilities: [...prev.responsibilities, trimmedResponsibility]
    }));
    setNewResponsibility('');
  };

  const handleRemoveResponsibility = (indexToRemove) => {
    setNewJob(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleAddEditResponsibility = () => {
    const trimmedResponsibility = editResponsibility.trim();
    if (!trimmedResponsibility) return;

    setEditJob(prev => ({
      ...prev,
      responsibilities: [...prev.responsibilities, trimmedResponsibility]
    }));
    setEditResponsibility('');
  };

  const handleRemoveEditResponsibility = (indexToRemove) => {
    setEditJob(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleMoveNewResponsibility = (index, direction) => {
    setNewJob(prev => {
      const list = [...prev.responsibilities];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...prev, responsibilities: list };
    });
  };

  const handleSaveNewRespEdit = (index) => {
    const trimmed = newRespEditingValue.trim();
    if (!trimmed) return;
    setNewJob(prev => {
      const list = [...prev.responsibilities];
      list[index] = trimmed;
      return { ...prev, responsibilities: list };
    });
    setNewRespEditingIndex(null);
    setNewRespEditingValue('');
  };

  const handleMoveEditResponsibility = (index, direction) => {
    setEditJob(prev => {
      const list = [...prev.responsibilities];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...prev, responsibilities: list };
    });
  };

  const handleSaveEditRespEdit = (index) => {
    const trimmed = editRespEditingValue.trim();
    if (!trimmed) return;
    setEditJob(prev => {
      const list = [...prev.responsibilities];
      list[index] = trimmed;
      return { ...prev, responsibilities: list };
    });
    setEditRespEditingIndex(null);
    setEditRespEditingValue('');
  };

  // Handle adding a new job/role
  const handleAddJob = async () => {
    try {
      const { data: newRole, error } = await supabase
        .from('roles')
        .insert([{
          role_title: newJob.role_title,
          level: newJob.level,
          department_id: newJob.department_id,
          purpose: newJob.purpose,
          responsibilities: formatResponsibilitiesForStorage(newJob.responsibilities),
          organization_id: organizationId,
        }])
        .select()
        .single();

      if (error) throw error;

      setData([...data, newRole]);
      setNewJob({
        role_title: '',
        level: '',
        department_id: newJob.department_id,
        purpose: '',
        responsibilities: []
      });
      setIsCreateRoleOpen(false);
      setNewResponsibility('');
    } catch (err) {
      console.error('Error adding job:', err.message);
    }
  };

  // Handle opening the edit modal
  const handleEdit = (role) => {
    setSaveError('');
    setEditJob({
      role_id: role.role_id,
      role_title: role.role_title || '',
      role_grade: role.role_grade || '',
      level: role.level || '',
      department_id: role.department_id || '',
      purpose: role.purpose || '',
      responsibilities: parseResponsibilities(role.responsibilities)
    });
    setEditResponsibility('');
    setEditRespEditingIndex(null);
    setEditRespEditingValue('');
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      const updatedPayload = {
        role_title: editJob.role_title,
        level: editJob.level,
        department_id: editJob.department_id,
        purpose: editJob.purpose,
        responsibilities: formatResponsibilitiesForStorage(editJob.responsibilities)
      };

      if (editJob.role_grade) {
        updatedPayload.role_grade = editJob.role_grade;
      }

      const { data: updatedRow, error } = await supabase
        .from('roles')
        .update(updatedPayload)
        .eq('role_id', editJob.role_id)
        .select()
        .single();

      if (error) throw error;

      setData(prev => prev.map(item =>
        item.role_id === editJob.role_id ? { ...item, ...updatedRow } : item
      ));
      setIsEditModalOpen(false);
      setEditJob({
        role_id: null,
        role_title: '',
        role_grade: '',
        level: '',
        department_id: '',
        purpose: '',
        responsibilities: []
      });
      setEditResponsibility('');
    } catch (err) {
      console.error('Error updating role:', err.message);
      setSaveError(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle opening the delete confirmation modal
  const handleDeleteClick = (role) => {
    setDeleteConfirm({ isOpen: true, role });
  };

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    if (!deleteConfirm.role) return;

    try {
      const { error } = await supabase
        .from('roles')
        .delete()
        .eq('role_id', deleteConfirm.role.role_id);

      if (error) throw error;

      setData(data.filter((r) => r.role_id !== deleteConfirm.role.role_id));
      setDeleteConfirm({ isOpen: false, role: null });
    } catch (err) {
      console.error('Error deleting role:', err.message);
      alert('Failed to delete role: ' + err.message);
    }
  };

  const handleDepartmentChange = (e) => {
    const deptId = e.target.value;
    setSelectedDepartment(deptId);
  };

  const handleNewJobDepartmentChange = (e) => {
    const deptId = e.target.value;
    setNewJob(prev => ({
      ...prev,
      department_id: deptId,
      role_title: '',
      level: '',
      purpose: '',
      responsibilities: []
    }));
    setNewResponsibility('');
  };

  return (
    <div className="functional-structure">
      <h1><i className="bi bi-diagram-3" />Functional Structure</h1>
      <p><strong>How is your function structured ? </strong>Set up the roles in your function here, and the persona description that best suits the role in your organisation.</p>
      <hr className='fucntional-strcture-line'></hr>
      
      {/* Accordion for Level Descriptions */}
      <div className="level-accordion">
        <button 
          className="accordion-header"
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
        >
          <span>Explore the level descriptions</span>
          <span className={`accordion-icon ${isAccordionOpen ? 'open' : ''}`}>▼</span>
        </button>
        {isAccordionOpen && (
          <div className="accordion-content">
            <div className="level-cards">
              {levels.map(level => (
                <div key={level.value} className="level-card">
                  <h4>{level.label}</h4>
                  <p>{level.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="create-role-header">
        <button
          className={`create-role-toggle-btn ${isCreateRoleOpen ? 'create-role-toggle-btn--open' : ''}`}
          onClick={() => setIsCreateRoleOpen(!isCreateRoleOpen)}
        >
          <span className="create-role-toggle-icon">{isCreateRoleOpen ? '−' : '+'}</span>
          <span>{isCreateRoleOpen ? 'Close' : 'Create New Role'}</span>
        </button>
      </div>

      {isCreateRoleOpen && (
        <div className="create-role-panel">
          <select
            className="input-box"
            value={newJob.department_id}
            onChange={handleNewJobDepartmentChange}
          >
            <option value="">Select Department</option>
            {departments.map(dept => (
              <option key={dept.department_id} value={dept.department_id}>
                {dept.depart_name}
              </option>
            ))}
          </select>

          <input
            className="input-box"
            type="text"
            placeholder="Job Title"
            value={newJob.role_title}
            onChange={(e) => setNewJob({ ...newJob, role_title: e.target.value })}
          />

          <select
            className="input-box"
            value={newJob.level}
            onChange={(e) => setNewJob({ ...newJob, level: e.target.value })}
          >
            <option value="">Level</option>
            {levels.map(level => (
              <option key={level.value} value={level.value}>
                {level.value}
              </option>
            ))}
          </select>

          <textarea
            className="input-box textarea-box"
            placeholder="Job Purpose"
            value={newJob.purpose}
            onChange={(e) => setNewJob({ ...newJob, purpose: e.target.value })}
          />

          <div className="responsibilities-section">
            <div className="responsibility-input-group">
              <input
                className="input-box responsibility-input"
                type="text"
                placeholder="Add Job Objective"
                value={newResponsibility}
                onChange={(e) => setNewResponsibility(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddResponsibility();
                  }
                }}
              />
              <button
                type="button"
                className="add-responsibility-button"
                onClick={handleAddResponsibility}
              >
                Add Objective
              </button>
            </div>

            {newJob.responsibilities.length > 0 && (
              <div className="responsibilities-list">
                {newJob.responsibilities.map((responsibility, index) => (
                  <div key={index} className="responsibility-tag">
                    <div className="resp-tag__order">
                      <button
                        type="button"
                        className="resp-tag__move-btn"
                        onClick={() => handleMoveNewResponsibility(index, 'up')}
                        disabled={index === 0}
                        aria-label="Move up"
                      >▲</button>
                      <button
                        type="button"
                        className="resp-tag__move-btn"
                        onClick={() => handleMoveNewResponsibility(index, 'down')}
                        disabled={index === newJob.responsibilities.length - 1}
                        aria-label="Move down"
                      >▼</button>
                    </div>
                    {newRespEditingIndex === index ? (
                      <input
                        className="resp-tag__input"
                        value={newRespEditingValue}
                        onChange={(e) => setNewRespEditingValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleSaveNewRespEdit(index); }
                          if (e.key === 'Escape') { setNewRespEditingIndex(null); }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="resp-tag__text">{responsibility}</span>
                    )}
                    <div className="resp-tag__actions">
                      {newRespEditingIndex === index ? (
                        <>
                          <button type="button" className="resp-tag__save-btn" onClick={() => handleSaveNewRespEdit(index)} aria-label="Save edit">✓</button>
                          <button type="button" className="resp-tag__cancel-btn" onClick={() => setNewRespEditingIndex(null)} aria-label="Cancel edit">✕</button>
                        </>
                      ) : (
                        <button type="button" className="resp-tag__edit-btn" onClick={() => { setNewRespEditingIndex(index); setNewRespEditingValue(responsibility); }} aria-label="Edit">✎</button>
                      )}
                      <button
                        type="button"
                        className="remove-responsibility-button"
                        onClick={() => handleRemoveResponsibility(index)}
                        aria-label="Remove"
                      >×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="create-role-actions">
            <button
              className="add-button"
              onClick={handleAddJob}
              disabled={!newJob.role_title || !newJob.level || !newJob.department_id}
            >
              Create Role
            </button>
            <button
              type="button"
              className="create-role-cancel-btn"
              onClick={() => {
                setIsCreateRoleOpen(false);
                setNewJob({ role_title: '', level: '', department_id: '', purpose: '', responsibilities: [] });
                setNewResponsibility('');
                setNewRespEditingIndex(null);
                setNewRespEditingValue('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="input-section-title">
        <h2>Functional Roles</h2>
      </div>
      
      <select
        className="department-dropdown"
        value={selectedDepartment}
        onChange={handleDepartmentChange}
      >
        <option value="">Select a department</option>
        {departments.map(dept => (
          <option key={dept.department_id} value={dept.department_id}>
            {dept.depart_name}
          </option>
        ))}
      </select>
      
      <table className="objective-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Edit Modal — portalled to document.body */}
      {isEditModalOpen && createPortal(
        <div
          className="modal"
          style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999}}
          onClick={() => setIsEditModalOpen(false)}
        >
          <div
            className="modal-content modal-edit-wide"
            style={{background:'white',borderRadius:'10px',width:'500px',maxWidth:'95%',maxHeight:'90vh',overflowY:'auto',padding:'24px 28px',zIndex:100000}}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Edit Role</h3>
              <button className="modal-close-btn" type="button" onClick={() => { setIsEditModalOpen(false); setSaveError(''); }}>×</button>
            </div>
            <p className="modal-subtitle">Update the details for this role across your organisation.</p>
            <hr className="modal-divider" />

            <label className="modal-label">Job Title</label>
            <input
              type="text"
              placeholder="Job Title"
              value={editJob.role_title}
              onChange={(e) => setEditJob({ ...editJob, role_title: e.target.value })}
            />

            <label className="modal-label">Level</label>
            <select
              value={editJob.level}
              onChange={(e) => setEditJob({ ...editJob, level: e.target.value })}
            >
              <option value="">Select Level</option>
              {levels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.value}
                </option>
              ))}
            </select>

            <label className="modal-label">Job Purpose</label>
            <textarea
              placeholder="Job Purpose"
              value={editJob.purpose}
              onChange={(e) => setEditJob({ ...editJob, purpose: e.target.value })}
            />

            <label className="modal-label">Responsibilities</label>
            <div className="modal-responsibilities-section">
              <div className="modal-responsibility-input-group">
                <input
                  type="text"
                  placeholder="Add a responsibility and press Add"
                  value={editResponsibility}
                  onChange={(e) => setEditResponsibility(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEditResponsibility();
                    }
                  }}
                />
                <button
                  type="button"
                  className="add-responsibility-button"
                  onClick={handleAddEditResponsibility}
                >
                  Add
                </button>
              </div>

              {editJob.responsibilities.length > 0 && (
                <div className="responsibilities-list">
                  {editJob.responsibilities.map((responsibility, index) => (
                    <div key={index} className="responsibility-tag">
                      <div className="resp-tag__order">
                        <button
                          type="button"
                          className="resp-tag__move-btn"
                          onClick={() => handleMoveEditResponsibility(index, 'up')}
                          disabled={index === 0}
                          aria-label="Move up"
                        >▲</button>
                        <button
                          type="button"
                          className="resp-tag__move-btn"
                          onClick={() => handleMoveEditResponsibility(index, 'down')}
                          disabled={index === editJob.responsibilities.length - 1}
                          aria-label="Move down"
                        >▼</button>
                      </div>
                      {editRespEditingIndex === index ? (
                        <input
                          className="resp-tag__input"
                          value={editRespEditingValue}
                          onChange={(e) => setEditRespEditingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); handleSaveEditRespEdit(index); }
                            if (e.key === 'Escape') { setEditRespEditingIndex(null); }
                          }}
                          autoFocus
                        />
                      ) : (
                        <span className="resp-tag__text">{responsibility}</span>
                      )}
                      <div className="resp-tag__actions">
                        {editRespEditingIndex === index ? (
                          <>
                            <button type="button" className="resp-tag__save-btn" onClick={() => handleSaveEditRespEdit(index)} aria-label="Save edit">✓</button>
                            <button type="button" className="resp-tag__cancel-btn" onClick={() => setEditRespEditingIndex(null)} aria-label="Cancel edit">✕</button>
                          </>
                        ) : (
                          <button type="button" className="resp-tag__edit-btn" onClick={() => { setEditRespEditingIndex(index); setEditRespEditingValue(responsibility); }} aria-label="Edit">✎</button>
                        )}
                        <button
                          type="button"
                          className="remove-responsibility-button"
                          onClick={() => handleRemoveEditResponsibility(index)}
                          aria-label="Remove"
                        >×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {saveError && <p className="org-edit-error">{saveError}</p>}
            <div className="modal-buttons">
              <button type="button" onClick={handleSaveEdit} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={() => { setIsEditModalOpen(false); setSaveError(''); }} disabled={isSaving}>Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal — portalled to document.body */}
      {deleteConfirm.isOpen && createPortal(
        <div
          className="modal"
          style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999}}
          onClick={() => setDeleteConfirm({ isOpen: false, role: null })}
        >
          <div
            className="modal-content modal-delete"
            style={{background:'white',borderRadius:'10px',width:'440px',maxWidth:'95%',padding:'24px 28px',zIndex:100000}}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-delete-header">
              <h3 className="modal-delete-title">Delete Role?</h3>
              <span className="modal-delete-item-name">"{deleteConfirm.role?.role_title}"</span>
            </div>
            <p className="modal-delete-warning">
              This action <strong>cannot be undone</strong>. Deleting this role will have the following impact:
            </p>
            <ul className="modal-delete-impacts">
              <li>All <strong>objectives</strong> linked to this role will be permanently removed.</li>
              <li>All <strong>competencies</strong> assigned to this role will be deleted.</li>
              <li>Any <strong>performance data</strong> associated with this role will be lost.</li>
            </ul>
            <div className="modal-buttons modal-delete-buttons">
              <button
                type="button"
                onClick={() => setDeleteConfirm({ isOpen: false, role: null })}
              >
                Cancel
              </button>
              <button type="button" className="btn-danger" onClick={handleConfirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default FunctionalStructure;